import React, {useCallback, useState} from 'react';

import {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import AnalysisGraphUtils from 'analysis-tool/flowchart/analysis-graph/AnalysisGraphUtils';
import nodePositionsGlobalState from 'analysis-tool/flowchart/nodePositionsGlobalState';
import {refreshAnalysis} from 'analysis-tool/polling/refreshPipeline';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {stepTypeIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {addStepWithInferredEdges} from 'pipelines/network/addStepWithInferredEdges';
import useCheckStepTypeEnabledForUser from 'pipelines/PipelineCapabilities';
import {PipelineStep, PipelineStepData} from 'pipelines/PipelineTypes';
import {getInputStepDatas, getLatestStepVersion, getPossibleEdges} from 'pipelines/StepConnectionUtils';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../../RightSidebarTypes';
import RightSidebarSkeleton, {RightSidebarWidth} from '../RightSidebarSkeleton';
import StepTypeSelector from './StepTypeSelector';

/**
 * Step types which cannot be added in-line from the right sidebar, even though the step type has a UI
 * schema and the `gating` section of the step type's YAML manifest allows the user to create it.
 */
const STEP_TYPES_THAT_CANNOT_BE_ADDED_FROM_RIGHT_SIDEBAR: ReadonlySet<PipelineStepType> = new Set([
  // Currently, CHART_VIEW steps can only be added from the left sidebar.
  PipelineStepType.CHART_VIEW,
]);

/**
 * True if the step type can be connected to `step` and `stepData`.
 */
function canConnectStepTypeToStepData(
  step: PipelineStep,
  stepData: PipelineStepData,
  newStepType: PipelineStepType
): boolean {
  const possibleEdges = step.inputStepDatas.find(({id}) => id === stepData.id)
    ? getPossibleEdges({
        fromStepType: newStepType,
        toStepType: step.stepType,
        toStepData: {systemName: stepData.name},
      })
    : getPossibleEdges({
        toStepType: newStepType,
        fromStepType: step.stepType,
        fromStepData: {systemName: stepData.name},
      });

  // There may be more than one possible connection to the next step, such as to either the `Left` or `Right`
  // input of a join. In this scenario, the `addStep` util will choose a valid edge, and the user can
  // drag-and-drop edges to fix any incorrect connections.
  return possibleEdges.length > 0;
}

/**
 * Add a new step to the pipeline, connected in-line to an existing step's `stepData`.
 *
 * Only step types which can be connected to `stepData` and which can be edited from `AnalysisFlowchart`
 * can be added.
 */
export default function AddStepRightSidebar({
  analysis: pipeline,
  onChangeSelectedRightSidebarItem,
  selectedRightSidebarItem,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.ADD_STEP>): JSX.Element {
  const {stepId, stepDataName, createBranch, position} = selectedRightSidebarItem;
  const [selectedStepType, setSelectedStepType] = useState<PipelineStepType | null>(null);

  const step = getStepFromAnalysis(pipeline, stepId);
  const stepData = [...step.inputStepDatas, ...step.outputStepDatas].find(({name}) => name === stepDataName);

  const checkStepTypeEnabledForUser = useCheckStepTypeEnabledForUser(pipeline.id);
  const filterStepTypes = useCallback(
    (stepType: PipelineStepType): boolean =>
      // Only add step types that are configurable in the generic way, and
      stepTypeIsConfigEditorSupported(stepType, getLatestStepVersion(stepType)) &&
      !STEP_TYPES_THAT_CANNOT_BE_ADDED_FROM_RIGHT_SIDEBAR.has(stepType) &&
      // can be connected to the selected step data, and
      stepData != null &&
      canConnectStepTypeToStepData(step, stepData, stepType) &&
      // are not source steps (in case we're connecting to an input step data), and
      getInputStepDatas(stepType).length > 0 &&
      // are enabled for this user.
      checkStepTypeEnabledForUser(stepType),
    [checkStepTypeEnabledForUser, step, stepData]
  );

  // Add the new step type when Next is clicked
  const onNext = async (): Promise<void> => {
    if (!selectedStepType) {
      return;
    }
    const {stepId} = await withLoadingScreen(
      addStepWithInferredEdges(pipeline, {stepType: selectedStepType, stepData, createBranch})
    );
    if (position) {
      nodePositionsGlobalState.updateState(pipeline.id, {
        ...nodePositionsGlobalState.getState(pipeline.id),
        [AnalysisGraphUtils.getNodeId({id: stepId})]: position,
      });
    }
    await withLoadingScreen(refreshAnalysis(pipeline.id));
    onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.EDIT_STEP, stepId});
  };

  return (
    <RightSidebarSkeleton
      title="Add step"
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: () => {
            onChangeSelectedRightSidebarItem(null);
          },
          dataTestAttrsElement: 'cancel',
        },
        {
          text: 'Next',
          onClick: onNext,
          dataTestAttrsElement: 'next',
          disabled: !selectedStepType,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.MEDIUM}
      dataTestAttrs={{
        component: 'AddStepRightSidebar',
        element: 'root',
      }}
    >
      <StepTypeSelector
        selectedStepType={selectedStepType}
        onSelectStepType={setSelectedStepType}
        filterStepTypes={filterStepTypes}
      />
    </RightSidebarSkeleton>
  );
}
